import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Instagram, Chrome, Star, Users, Award, TrendingUp, Shield, Clock, DollarSign, CheckCircle, Menu, X } from 'lucide-react';
import car2 from './images/swift_dzire.jpg';
import car3 from './images/ertiga.jpg';
import car4 from './images/innova-crysta.jpg';
import car1 from './images/carens.jpg';

// SEO Head Component
const SEOHead = () => {
  useEffect(() => {
    // Set document title
    document.title = "Best Cab Service in Pune & Pimpri Chinchwad | DestinyCabs - 24/7 Taxi Booking";

    // Create or update meta tags
    const metaTags = [
      { name: 'description', content: 'Book reliable cabs in Pune, Pimpri Chinchwad & across Maharashtra. DestinyCabs offers 24/7 taxi service, affordable rates, professional drivers. Call +91 7498260509 for instant booking.' },
      { name: 'keywords', content: 'cabs from pune, taxi in pune, pimpri chinchwad cab service, pune to mumbai cab, pune taxi booking, cab service pune, outstation cabs pune, local taxi pune, airport taxi pune, pune cab rates, best cab service pune, affordable taxi pune, pune to solapur cab, pune to satara taxi, maharashtra cab service, destinycabs, destiny cabs pune' },
      { name: 'author', content: 'DestinyCabs' },
      { name: 'robots', content: 'index, follow' },
      { name: 'googlebot', content: 'index, follow' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { property: 'og:title', content: 'Best Cab Service in Pune & Pimpri Chinchwad | DestinyCabs' },
      { property: 'og:description', content: 'Reliable 24/7 cab service in Pune, Pimpri Chinchwad & Maharashtra. Professional drivers, clean vehicles, transparent pricing. Book now!' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://destinycabservices.com' },
      { property: 'og:site_name', content: 'DestinyCabs' },
      { property: 'og:locale', content: 'en_IN' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Best Cab Service in Pune & Pimpri Chinchwad | DestinyCabs' },
      { name: 'twitter:description', content: 'Book reliable cabs in Pune & Pimpri Chinchwad. 24/7 service, affordable rates. Call +91 7498260509' },
      { name: 'geo.region', content: 'IN-MH' },
      { name: 'geo.placename', content: 'Pune, Pimpri Chinchwad, Maharashtra' },
      { name: 'geo.position', content: '18.5204;73.8567' },
      { name: 'ICBM', content: '18.5204, 73.8567' }
    ];

    metaTags.forEach(tag => {
      let element = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        if (tag.name) element.setAttribute('name', tag.name);
        if (tag.property) element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });

    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://destinycabservices.com');

    // Add structured data (JSON-LD)
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "@id": "https://destinycabservices.com/#business",
          "name": "DestinyCabs",
          "alternateName": "Destiny Cab Services",
          "description": "Professional cab and taxi service in Pune, Pimpri Chinchwad and across Maharashtra. Offering reliable, affordable 24/7 transportation services.",
          "url": "https://destinycabservices.com",
          "telephone": "+917498260509",
          "email": "travelservicedestiny@gmail.com",
          "priceRange": "$$",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Pune",
            "addressLocality": "Pune",
            "addressRegion": "Maharashtra",
            "postalCode": "411001",
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "18.5204",
            "longitude": "73.8567"
          },
          "areaServed": [
            { "@type": "City", "name": "Pune" },
            { "@type": "City", "name": "Pimpri Chinchwad" },
            { "@type": "City", "name": "Mumbai" },
            { "@type": "State", "name": "Maharashtra" }
          ],
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "00:00",
            "closes": "23:59"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.7",
            "reviewCount": "250",
            "bestRating": "5",
            "worstRating": "1"
          },
          "image": "https://destinycabservices.com/og-image.jpg",
          "sameAs": [
            "https://www.instagram.com/destiny_cabs_/",
            "https://destinycabservices.com"
          ]
        },
        {
          "@type": "Service",
          "@id": "https://destinycabservices.com/#service",
          "serviceType": "Taxi and Cab Service",
          "provider": { "@id": "https://destinycabservices.com/#business" },
          "areaServed": { "@type": "State", "name": "Maharashtra" },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Cab Services",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Local Taxi Service in Pune" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Outstation Cab Service from Pune" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Pimpri Chinchwad Taxi Service" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Airport Transfer Pune" } }
            ]
          }
        },
        {
          "@type": "WebSite",
          "@id": "https://destinycabservices.com/#website",
          "url": "https://destinycabservices.com",
          "name": "DestinyCabs",
          "description": "Book cabs in Pune, Pimpri Chinchwad & Maharashtra",
          "publisher": { "@id": "https://destinycabservices.com/#business" },
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://destinycabservices.com/?s={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }
      ]
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);

  }, []);

  return null;
};

// ---------- Shared brand mark ----------
const Logo = ({ variant = 'dark' }) => {
  return (
    <div className="flex items-center space-x-3">
      <div className="relative flex-shrink-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl blur-md opacity-50"></div>
        <div className="relative bg-gradient-to-br from-blue-600 via-blue-600 to-cyan-500 p-2.5 rounded-2xl shadow-lg shadow-blue-500/30">
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M5 16.5V12l1.6-4.8A2 2 0 0 1 8.5 6h7a2 2 0 0 1 1.9 1.2L19 12v4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <rect x="3.5" y="12" width="17" height="5.5" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
            <circle cx="7.5" cy="17.7" r="1.4" fill="currentColor" />
            <circle cx="16.5" cy="17.7" r="1.4" fill="currentColor" />
            <path d="M3.5 14.2h17" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        </div>
      </div>
      <div className="flex flex-col leading-none">
        <span className={`text-2xl font-extrabold tracking-tight ${variant === 'light' ? 'text-white' : 'bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent'}`}>
          DestinyCabs
        </span>
        <span className={`text-[10px] font-semibold tracking-widest uppercase mt-0.5 ${variant === 'light' ? 'text-gray-400' : 'text-gray-400'}`}>
          Pune • Maharashtra
        </span>
      </div>
    </div>
  );
};

const SectionBadge = ({ children }) => (
  <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
    <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
    {children}
  </span>
);

// ---------- Floating action buttons (Call above WhatsApp) ----------
const FloatingActionButtons = () => {
  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-4">
      <a
        href="tel:+917498260509"
        aria-label="Call DestinyCabs now"
        className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-blue-500 text-white shadow-2xl shadow-blue-500/40 hover:scale-110 transition-all duration-300"
      >
        <span className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-20"></span>
        <Phone className="w-6 h-6 relative" />
        <span className="pointer-events-none absolute right-16 whitespace-nowrap bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          Call Now
        </span>
      </a>

      <a
        href="https://wa.me/917498260509"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp for cab booking"
        className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white shadow-2xl shadow-green-500/40 hover:scale-110 transition-all duration-300"
      >
        <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-20"></span>
        <svg className="w-7 h-7 relative" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
        <span className="pointer-events-none absolute right-16 whitespace-nowrap bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          WhatsApp
        </span>
      </a>
    </div>
  );
};

// Main App Component
const App = () => {
  const [currentPage, setCurrentPage] = useState('home');

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogoClick = () => {
    setCurrentPage('home');
    setTimeout(scrollToTop, 100);
  };

  const handleHomeClick = () => {
    setCurrentPage('home');
    setTimeout(scrollToTop, 100);
  };

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-gray-50">
        {currentPage === 'home' ? (
          <HomePage
            setCurrentPage={setCurrentPage}
            onHomeClick={handleHomeClick}
            onLogoClick={handleLogoClick}
          />
        ) : (
          <QuickBookingPage
            setCurrentPage={setCurrentPage}
            onHomeClick={handleHomeClick}
            onLogoClick={handleLogoClick}
          />
        )}
        <FloatingActionButtons />
      </div>
    </>
  );
};

// Home Page
const HomePage = ({ setCurrentPage, onHomeClick, onLogoClick }) => {
  return (
    <main>
      <Navbar onHomeClick={onHomeClick} onLogoClick={onLogoClick} />
      <Hero setCurrentPage={setCurrentPage} />
      <AboutUs />
      <ServiceAreas />
      <CarGallery />
      <Stats />
      <Reviews />
      <WhyChooseUs />
      <FAQ />
      <Footer />
    </main>
  );
};

// Navbar Component
const Navbar = ({ onHomeClick, onLogoClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const NavLink = ({ onClick, children }) => (
    <button onClick={onClick} className="relative text-gray-700 hover:text-blue-600 transition-colors font-medium group py-1">
      {children}
      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 group-hover:w-full transition-all duration-300"></span>
    </button>
  );

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-lg shadow-lg' : 'bg-white/70 backdrop-blur-md'}`} role="navigation" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <button onClick={onLogoClick} className="hover:opacity-90 transition-opacity" aria-label="DestinyCabs Home">
            <Logo />
          </button>

          <div className="hidden md:flex items-center space-x-8">
            <NavLink onClick={onHomeClick}>Home</NavLink>
            <NavLink onClick={() => scrollToSection('about')}>About</NavLink>
            <NavLink onClick={() => scrollToSection('services')}>Services</NavLink>
            <NavLink onClick={() => scrollToSection('contact')}>Contact</NavLink>

            <a
              href="tel:+917498260509"
              className="relative flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white pl-4 pr-5 py-2.5 rounded-full hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300 font-semibold overflow-hidden group"
              aria-label="Call DestinyCabs now"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
              <Phone className="w-4 h-4 relative" />
              <span className="relative">Call Now</span>
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-blue-50 transition-colors"
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6 text-blue-600" /> : <Menu className="w-6 h-6 text-blue-600" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-lg border-t border-gray-100 shadow-xl">
          <div className="px-4 py-4 space-y-1">
            <button onClick={onHomeClick} className="block w-full text-left text-gray-700 hover:text-blue-600 hover:bg-blue-50 font-medium py-3 px-3 rounded-lg transition-colors">Home</button>
            <button onClick={() => scrollToSection('about')} className="block w-full text-left text-gray-700 hover:text-blue-600 hover:bg-blue-50 font-medium py-3 px-3 rounded-lg transition-colors">About</button>
            <button onClick={() => scrollToSection('services')} className="block w-full text-left text-gray-700 hover:text-blue-600 hover:bg-blue-50 font-medium py-3 px-3 rounded-lg transition-colors">Services</button>
            <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-700 hover:text-blue-600 hover:bg-blue-50 font-medium py-3 px-3 rounded-lg transition-colors">Contact</button>
            <a href="tel:+917498260509" className="flex items-center justify-center gap-2 w-full text-center bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-full font-semibold mt-2">
              <Phone className="w-4 h-4" /> Call Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const Hero = ({ setCurrentPage }) => {
  return (
    <section className="relative pt-32 pb-20 bg-gradient-to-br from-blue-50 via-white to-cyan-50 overflow-hidden">
      {/* decorative gradient blobs */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 bg-blue-300/30 rounded-full blur-3xl"></div>
      <div className="pointer-events-none absolute top-1/3 -right-32 w-[28rem] h-[28rem] bg-cyan-300/30 rounded-full blur-3xl"></div>
      <div className="pointer-events-none absolute bottom-0 left-1/4 w-72 h-72 bg-blue-200/30 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <article className="order-2 lg:order-1">
            <SectionBadge>24/7 Cab Booking · Pune</SectionBadge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Book Cabs from Pune & Pimpri Chinchwad —{' '}
              <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                Best Cab Service in Maharashtra
              </span>
            </h1>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Looking for reliable cabs from Pune or Pimpri Chinchwad? DestinyCabs is your trusted partner for local and outstation taxi services across Maharashtra. We offer 24/7 cab booking with professional drivers, well-maintained vehicles, and transparent pricing.
            </p>
            <p className="text-base text-gray-600 mb-8 leading-relaxed">
              Whether you need a taxi in Pune for airport transfers, Pimpri Chinchwad local cabs, or outstation trips to Mumbai, Solapur, Satara, or Nashik, we've got you covered. Book your Pune cab now and experience comfortable, safe travel at affordable rates.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <a
                href="tel:+917498260509"
                className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white pl-5 pr-8 py-3.5 rounded-full hover:shadow-2xl hover:shadow-blue-500/40 transition-all duration-300 font-semibold overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
                <span className="relative flex items-center justify-center w-10 h-10 rounded-full bg-white/20">
                  <Phone className="w-4 h-4" />
                </span>
                <span className="relative flex flex-col items-start leading-tight">
                  <span className="text-xs font-medium text-blue-100">+91 7498260509</span>
                  <span className="text-base">Book Cab Now</span>
                </span>
              </a>
              <button
                onClick={() => {
                  const element = document.getElementById('about');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-3.5 rounded-full hover:bg-blue-50 transition-all duration-300 font-semibold"
              >
                Learn More
              </button>
            </div>

            {/* trust badges */}
            <div className="flex flex-wrap gap-3">
              {[
                { icon: Star, text: '4.7/5 Google Rating' },
                { icon: Award, text: '12+ Years Experience' },
                { icon: Users, text: '10,000+ Happy Rides' }
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-2 bg-white shadow-sm border border-gray-100 rounded-full px-4 py-2 text-sm font-medium text-gray-700">
                  <badge.icon className="w-4 h-4 text-blue-600 fill-blue-600" />
                  {badge.text}
                </div>
              ))}
            </div>
          </article>

          <aside className="order-1 lg:order-2">
            <BookingForm setCurrentPage={setCurrentPage} />
          </aside>
        </div>
      </div>
    </section>
  );
};

// Booking Form Component
const BookingForm = ({ setCurrentPage }) => {
  const [formData, setFormData] = useState({ from: '', to: '', journey: '' });

  const cities = ['Pune', 'Pimpri Chinchwad', 'Mumbai', 'Solapur', 'Satara', 'Nashik', 'Sangli', 'Kolhapur'];
  const journeyTypes = ['One Way', 'Round Trip'];

  const handleSubmit = () => {
    if (formData.from && formData.to && formData.journey) {
      setCurrentPage('booking');
      window.scrollTo(0, 0);
    }
  };

  return (
    <div className="relative">
      <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-[2rem] blur-lg opacity-20"></div>
      <div className="relative bg-white rounded-[1.75rem] shadow-2xl p-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Book Your Cab from Pune</h2>
        <p className="text-sm text-gray-500 mb-6">Instant confirmation · Best rates guaranteed</p>
        <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div>
            <label htmlFor="pickup-location" className="block text-sm font-semibold text-gray-700 mb-2">From (Pickup Location)</label>
            <div className="relative">
              <MapPin className="w-5 h-5 text-blue-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                id="pickup-location"
                value={formData.from}
                onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                className="w-full pl-11 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors appearance-none bg-white"
                required
              >
                <option value="">Select pickup city</option>
                {cities.map(city => <option key={city} value={city}>{city}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="drop-location" className="block text-sm font-semibold text-gray-700 mb-2">To (Drop Location)</label>
            <div className="relative">
              <MapPin className="w-5 h-5 text-cyan-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                id="drop-location"
                value={formData.to}
                onChange={(e) => setFormData({ ...formData, to: e.target.value })}
                className="w-full pl-11 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors appearance-none bg-white"
                required
              >
                <option value="">Select drop city</option>
                {cities.map(city => <option key={city} value={city}>{city}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="journey-type" className="block text-sm font-semibold text-gray-700 mb-2">Journey Type</label>
            <div className="relative">
              <Clock className="w-5 h-5 text-blue-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                id="journey-type"
                value={formData.journey}
                onChange={(e) => setFormData({ ...formData, journey: e.target.value })}
                className="w-full pl-11 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors appearance-none bg-white"
                required
              >
                <option value="">Select journey type</option>
                {journeyTypes.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="w-full relative overflow-hidden bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-blue-500/40 transition-all duration-300 transform hover:scale-[1.02] group"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
            <span className="relative">Search Cabs</span>
          </button>
        </form>
      </div>
    </div>
  );
};

// Quick Booking Page
const QuickBookingPage = ({ onHomeClick, onLogoClick }) => {
  return (
    <>
      <Navbar onHomeClick={onHomeClick} onLogoClick={onLogoClick} />
      <main className="min-h-screen pt-28 pb-16 bg-gradient-to-br from-blue-50 via-white to-cyan-50 relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 bg-blue-300/20 rounded-full blur-3xl"></div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="bg-white rounded-[1.75rem] shadow-2xl p-8 md:p-12 text-center border border-gray-100">
            <div className="bg-gradient-to-br from-blue-600 to-cyan-500 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/30">
              <Phone className="w-10 h-10 text-white" />
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Book Pune Cab Service Instantly<br />
              <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Call Us Now for Best Rates!</span>
            </h1>

            <p className="text-gray-600 mb-6 text-lg">
              Need a cab from Pune or Pimpri Chinchwad? Our online booking system is being upgraded to serve you better.
            </p>

            <p className="text-gray-700 mb-8">
              For immediate taxi booking in Pune, call our 24/7 support team. We provide instant confirmation for local and outstation cabs!
            </p>

            <a href="tel:+917498260509" className="inline-block text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent mb-2">
              📞 +91 7498260509
            </a>

            <p className="text-green-600 font-semibold mb-8">Available 24/7 for Cab Booking</p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a href="tel:+917498260509" className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-full font-bold hover:shadow-2xl hover:shadow-blue-500/30 transition-all duration-300 inline-flex items-center justify-center">
                <Phone className="w-5 h-5 mr-2" />
                Call for Cab Booking
              </a>
              <button onClick={onHomeClick} className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-4 rounded-full font-bold hover:bg-blue-50 transition-all duration-300">
                Go Back Home
              </button>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 space-y-3">
              <div className="flex items-center justify-center gap-2 text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Best cab rates in Pune</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Instant taxi confirmation</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Professional drivers guaranteed</span>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
};

// About Us Section
const AboutUs = () => {
  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <SectionBadge>Who We Are</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">About DestinyCabs - Best Cab Service in Pune & Pimpri Chinchwad</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto rounded-full"></div>
        </header>

        <article className="max-w-4xl mx-auto">
          <p className="text-lg text-gray-700 leading-relaxed text-center mb-6">
            DestinyCabs is the leading cab service provider in Pune, Pimpri Chinchwad, and across Maharashtra. With over 12 years of experience in taxi services, we understand what makes a journey comfortable and safe. Whether you're looking for cabs from Pune to Mumbai, local taxi service in Pimpri Chinchwad, or outstation travel, we're your reliable partner.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed text-center">
            Our cab services include airport transfers, intercity travel, local Pune taxi booking, corporate transportation, and more. We offer transparent pricing with no hidden charges, professional verified drivers, well-maintained AC vehicles, and 24/7 customer support. When you book a cab with DestinyCabs, you choose reliability, affordability, and comfort. Experience the best taxi service in Pune and Pimpri Chinchwad today!
          </p>
        </article>
      </div>
    </section>
  );
};

// Service Areas Section
const ServiceAreas = () => {
  const areas = [
    { name: 'Pune', description: 'Local and outstation cab service in Pune city with 24/7 availability', routes: ['Pune to Mumbai', 'Pune Airport', 'Pune Local'] },
    { name: 'Pimpri Chinchwad', description: 'Reliable taxi service covering all areas of Pimpri Chinchwad', routes: ['PCMC Local', 'PCMC To Mumbai Airport', 'PCMC To Pune Airport'] },
    { name: 'Mumbai', description: 'Comfortable cabs from Pune to Mumbai and return trips', routes: ['Pune to Mumbai', 'Mumbai Airport', 'Andheri to Pune'] },
    { name: 'Solapur', description: 'Regular cab service between Pune and Solapur', routes: ['Pune to Solapur', 'Solapur to Pune'] },
    { name: 'Satara', description: 'Affordable taxi booking from Pune to Satara', routes: ['Pune to Satara', 'Satara Local'] },
    { name: 'Nashik', description: 'Comfortable outstation cabs from Pune to Nashik', routes: ['Pune to Nashik', 'Nashik to Pune'] }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <SectionBadge>Service Areas</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Service Areas - Cabs Across Maharashtra</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto rounded-full"></div>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            We provide cab services across major cities in Maharashtra. Book your taxi for local or outstation travel.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {areas.map((area, index) => (
            <article key={index} className="group bg-white rounded-2xl shadow-md border border-gray-100 p-6 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-1 hover:border-blue-100">
              <div className="flex items-center mb-4">
                <div className="bg-gradient-to-br from-blue-600 to-cyan-500 w-11 h-11 rounded-xl flex items-center justify-center mr-3 shadow-md shadow-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{area.name} Cab Service</h3>
              </div>
              <p className="text-gray-600 mb-4">{area.description}</p>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">Popular Routes:</p>
                {area.routes.map((route, idx) => (
                  <div key={idx} className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    {route}
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// Car Gallery Section
const CarGallery = () => {
  const carImages = [
    { src: car1, alt: "Kia Carens - Luxury Sedan Cab Service in Pune", type: "Premium SUV (6+1) Diesel" },
    { src: car2, alt: "Swift - Affordable Hatchback Taxi in Pimpri Chinchwad", type: "Premium Sedan (4+1)" },
    { src: car3, alt: "Ertiga - Comfortable MUV Cab for Pune to Mumbai", type: "Premium MUV(6+1) CNG" },
    { src: car4, alt: "Innova Crysta - Premium SUV Taxi Service Pune", type: "Luxury SUV (6+1) Diesel" }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <SectionBadge>Our Fleet</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Premium Cab Fleet in Pune</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto rounded-full"></div>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            Choose from our well-maintained fleet of cabs. From hatchbacks to luxury SUVs, we have the perfect taxi for every journey in Pune and Maharashtra.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {carImages.map((car, index) => (
            <figure key={index} className="relative overflow-hidden rounded-2xl shadow-xl transform hover:scale-105 transition-all duration-500 hover:shadow-2xl group">
              <img
                src={car.src}
                alt={car.alt}
                className="w-full h-64 md:h-80 object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              <figcaption className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <p className="text-lg font-semibold">{car.type}</p>
                </div>
              </figcaption>
              <div className="absolute top-4 left-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                Available for Booking
              </div>
            </figure>
          ))}
        </div>

        <div className="mt-12 max-w-3xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: Shield, title: 'Regular Sanitization', desc: 'All cabs sanitized after every trip' },
              { icon: Clock, title: '24/7 Availability', desc: 'Cabs available round the clock in Pune' },
              { icon: CheckCircle, title: 'AC Comfort', desc: 'All vehicles with AC for comfortable rides' }
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const Stats = () => {
  const stats = [
    { icon: Users, value: '10,000+', label: 'Happy Customers in Pune', color: 'from-blue-500 to-blue-600' },
    { icon: Award, value: '12+', label: 'Years of Cab Service', color: 'from-green-500 to-green-600' },
    { icon: TrendingUp, value: '20+', label: 'Professional Drivers', color: 'from-purple-500 to-purple-600' },
    { icon: Star, value: '4.7/5', label: 'Google Reviews', color: 'from-orange-500 to-orange-600' }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <article key={index} className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 text-center transform hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
              <div className={`bg-gradient-to-r ${stat.color} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                <stat.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</h3>
              <p className="text-gray-600 font-medium">{stat.label}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const Reviews = () => {
  const reviews = [
    { name: 'Hrishikesh Tamhane', rating: 5, location: 'Pune', text: 'Best cab service in Pune! The driver was punctual, professional, and the car was spotless. Highly recommend DestinyCabs for intercity travel from Pune.', date: '2024-12-15' },
    { name: 'Sakshi Sharma', rating: 5, location: 'Pimpri Chinchwad', text: 'Excellent taxi service in Pimpri Chinchwad! Affordable rates and amazing customer support. They made my Mumbai to Pune trip so comfortable.', date: '2024-12-10' },
    { name: 'Rohit Deshmukh', rating: 5, location: 'Pune', text: 'Very reliable and safe cab service. I always book DestinyCabs for my family trips from Pune. The drivers are courteous and the booking process is smooth.', date: '2024-12-05' }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <SectionBadge>Testimonials</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Customer Reviews - Pune Cab Service</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto rounded-full"></div>
          <p className="text-gray-600 mt-4">See what our customers say about our taxi service in Pune and Pimpri Chinchwad</p>
        </header>

        <div className="grid md:grid-cols-3 gap-8">
          {reviews.map((review, index) => (
            <article key={index} className="relative bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-md border border-gray-100 p-6 transform hover:scale-105 transition-all duration-300 hover:shadow-2xl" itemScope itemType="https://schema.org/Review">
              <span className="absolute top-4 right-5 text-6xl text-blue-100 font-serif leading-none select-none">"</span>
              <div className="relative flex mb-4" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
                <meta itemProp="ratingValue" content={review.rating} />
                <meta itemProp="bestRating" content="5" />
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="relative text-gray-700 mb-4 italic" itemProp="reviewBody">"{review.text}"</p>
              <div itemProp="author" itemScope itemType="https://schema.org/Person">
                <p className="font-bold text-gray-900">— <span itemProp="name">{review.name}</span></p>
                <p className="text-sm text-gray-600">{review.location}</p>
              </div>
              <meta itemProp="datePublished" content={review.date} />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUs = () => {
  const features = [
    { icon: Shield, text: 'Professional & verified drivers for safe cab rides in Pune' },
    { icon: Star, text: 'Clean and comfortable AC cabs for all routes' },
    { icon: DollarSign, text: 'Transparent pricing - No hidden charges for taxi booking' },
    { icon: Clock, text: '24/7 customer support for cab service in Pune' },
    { icon: CheckCircle, text: 'On-time pickup & drop for all cab bookings' }
  ];

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <SectionBadge>Our Promise</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose DestinyCabs - Best Taxi Service in Pune?</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto rounded-full"></div>
        </header>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <article key={index} className="group bg-white rounded-2xl shadow-md border border-gray-100 p-6 flex items-start space-x-4 transform hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
              <div className="bg-gradient-to-br from-blue-600 to-cyan-500 p-3 rounded-xl flex-shrink-0 shadow-md shadow-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                <feature.icon className="w-6 h-6 text-white" />
              </div>
              <p className="text-gray-700 font-medium pt-2">{feature.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section (accordion)
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    { question: 'How do I book a cab from Pune or Pimpri Chinchwad?', answer: 'You can book a cab by calling us at +91 7498260509. Our 24/7 customer support team will help you book your taxi instantly for local or outstation travel.' },
    { question: 'What areas do you cover for cab service?', answer: 'We provide cab services in Pune, Pimpri Chinchwad, and across Maharashtra including Mumbai, Solapur, Satara, Nashik, Sangli, and Kolhapur. We offer both local and outstation taxi services.' },
    { question: 'Are your cab rates transparent?', answer: 'Yes, we offer completely transparent pricing with no hidden charges. You will know the exact fare before booking your cab from Pune or any other location.' },
    { question: 'Do you provide airport taxi service in Pune?', answer: 'Yes, we provide reliable airport transfer services to and from Pune Airport. Book your airport cab in advance for on-time pickup and comfortable travel.' },
    { question: 'Are your drivers professional and verified?', answer: 'All our cab drivers are professionally trained, verified, and experienced. Your safety and comfort are our top priorities for every taxi ride.' }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-blue-50/40">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <SectionBadge>Got Questions?</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions - Pune Cab Service</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto rounded-full"></div>
        </header>

        <div className="space-y-4" itemScope itemType="https://schema.org/FAQPage">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <article
                key={index}
                className={`bg-white rounded-2xl shadow-md border transition-all duration-300 ${isOpen ? 'border-blue-200 shadow-xl shadow-blue-500/10' : 'border-gray-100'}`}
                itemScope itemProp="mainEntity" itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  className="w-full flex items-center justify-between gap-4 text-left p-6"
                  aria-expanded={isOpen}
                >
                  <h3 className="text-lg font-bold text-gray-900" itemProp="name">{faq.question}</h3>
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-gradient-to-br from-blue-600 to-cyan-500 text-white' : 'bg-blue-50 text-blue-600'}`}>
                    <X className="w-4 h-4 transition-transform duration-300" style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(45deg)' }} />
                  </span>
                </button>
                {isOpen && (
                  <div className="px-6 pb-6" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p className="text-gray-600 leading-relaxed" itemProp="text">{faq.answer}</p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer id="contact" className="relative bg-gray-900 text-white py-12">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <div className="mb-4">
              <Logo variant="light" />
            </div>
            <p className="text-gray-400 mb-4">Your trusted partner for safe and comfortable cab journeys across Maharashtra. Best taxi service in Pune and Pimpri Chinchwad.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4">Contact DestinyCabs</h3>
            <address className="space-y-3 not-italic">
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-blue-400" />
                <span>Adesh Jagtap</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-blue-400" />
                <a href="tel:+917498260509" className="hover:text-blue-400 transition-colors" aria-label="Call DestinyCabs">
                  +91 7498260509
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-blue-400" />
                <a href="mailto:travelservicedestiny@gmail.com" className="hover:text-blue-400 transition-colors" aria-label="Email DestinyCabs">
                  travelservicedestiny@gmail.com
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-blue-400" />
                <span>Marunji, Pune, Maharashtra, India</span>
              </div>
            </address>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4">Follow Us</h3>
            <div className="flex space-x-4 mb-6">
              <a href="https://www.instagram.com/destiny_cabs_/?igsh=MXE0YWgyZDJoNDcxdQ%3D%3D#" target="_blank" rel="noopener noreferrer" className="bg-pink-600 p-3 rounded-full hover:bg-pink-700 transition-colors hover:scale-110 duration-300" aria-label="Follow us on Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://destinycabservices.com" target="_blank" rel="noopener noreferrer" className="bg-gray-700 p-3 rounded-full hover:bg-gray-600 transition-colors hover:scale-110 duration-300" aria-label="Visit our website">
                <Chrome className="w-5 h-5" />
              </a>
            </div>
            <div className="space-y-2 text-sm text-gray-400">
              <p>Service Areas:</p>
              <p>Pune | Pimpri Chinchwad | Mumbai | Solapur | Satara | Nashik</p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center">
          <p className="text-gray-400 mb-2">© {new Date().getFullYear()} DestinyCabs. All rights reserved.</p>
          <p className="text-gray-500 text-sm">
            Best Cab Service in Pune | Taxi Booking Pune | Pimpri Chinchwad Cab Service | Outstation Cabs Maharashtra
          </p>
        </div>
      </div>
    </footer>
  );
};

export default App;